import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchGallery();
  }, [navigate]);

  const fetchGallery = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/gallery`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGallery(data);
      }
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateImage = (index, field, value) => {
    const updated = [...gallery];
    updated[index] = { ...updated[index], [field]: value };
    setGallery(updated);
  };

  const handleAddImage = () => {
    const newId = gallery.length > 0 ? Math.max(...gallery.map(img => img.id)) + 1 : 1;
    setGallery([...gallery, {
      id: newId,
      url: '',
      title: '',
      description: '',
      fullWidthRow: false
    }]);
  };

  const handleDeleteImage = async (id, index) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${BACKEND_URL}/api/admin/gallery/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const updated = [...gallery];
      updated.splice(index, 1);
      setGallery(updated);
      toast.success('Image deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      for (const image of gallery) {
        if (image.url) { // Only save if URL is not empty
          await fetch(`${BACKEND_URL}/api/admin/gallery/${image.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(image)
          });
        }
      }
      
      toast.success('Gallery updated successfully!');
      fetchGallery(); // Refresh to get clean data
    } catch (error) {
      toast.error('Failed to update gallery');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="icon" className="text-white border-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Gallery Management</h1>
              <p className="text-cyan-100">Manage gallery images and descriptions</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddImage} variant="outline" className="text-white border-white hover:bg-white/10">
              <Plus className="w-4 h-4 mr-2" />
              Add Image
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-white text-cyan-600 hover:bg-cyan-50">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {gallery.map((image, index) => (
            <Card key={image.id}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Image #{image.id}</CardTitle>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteImage(image.id, index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {image.url && (
                  <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                    <img src={image.url} alt={image.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div>
                  <Label>Image URL</Label>
                  <Input
                    value={image.url}
                    onChange={(e) => handleUpdateImage(index, 'url', e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input
                    value={image.title}
                    onChange={(e) => handleUpdateImage(index, 'title', e.target.value)}
                    placeholder="Image title"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={image.description}
                    onChange={(e) => handleUpdateImage(index, 'description', e.target.value)}
                    placeholder="Image description"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {gallery.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No images in gallery yet</p>
            <Button onClick={handleAddImage} className="bg-cyan-600 hover:bg-cyan-700">
              <Plus className="w-4 h-4 mr-2" />
              Add First Image
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;
